import { useEffect, useRef, useState } from 'preact/hooks';
import { IcoChevronDown, IcoSearch } from '@components/icons';

type Course = {
  id: string;
  title: string;
  createdAt: string;
  studentCount: number;
  priceCount: number;
  hasCampaign: boolean;
  isBundle?: boolean;
  guideName?: string;
};

type Props = {
  courses: Course[];
  value: string;
  onChange(courseId: string): void;
  excludeId?: string;
  placeholder?: string;
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function BundleBadge() {
  return (
    <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
      Bundle
    </span>
  );
}

export function CourseSelect({ courses, value, onChange, excludeId, placeholder }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = courses
    .filter((c) => c.id !== excludeId)
    .filter((c) => {
      if (!search) {
        return true;
      }
      return c.title.toLowerCase().includes(search.toLowerCase());
    });

  const selected = courses.find((c) => c.id === value);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  function handleSelect(courseId: string) {
    onChange(courseId);
    setOpen(false);
    setSearch('');
  }

  return (
    <div class="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        class={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2.5 text-sm text-left transition-colors ${
          open
            ? 'border-indigo-500 ring-1 ring-indigo-500'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        {selected ? (
          <div class="flex items-center gap-2 min-w-0">
            <span class="font-medium text-gray-900 truncate">{selected.title}</span>
            {selected.isBundle && <BundleBadge />}
            {selected.guideName && (
              <span class="text-xs text-gray-500 flex-shrink-0">by {selected.guideName}</span>
            )}
            {selected.priceCount > 0 && (
              <span class="text-xs text-gray-400 flex-shrink-0">
                · {selected.priceCount} {selected.priceCount === 1 ? 'price' : 'prices'}
              </span>
            )}
          </div>
        ) : (
          <span class="text-gray-400">{placeholder || 'Select a course...'}</span>
        )}
        <IcoChevronDown
          class={`size-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div class="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div class="p-2 border-b border-gray-100">
            <div class="relative">
              <IcoSearch class="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                placeholder="Search courses..."
                class="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-indigo-400"
              />
            </div>
          </div>

          <div class="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div class="px-4 py-6 text-center text-sm text-gray-400">
                {search ? 'No courses match your search' : 'No courses available'}
              </div>
            ) : (
              filtered.map((course) => {
                const isSelected = course.id === value;
                const noPrices = course.priceCount === 0;
                return (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => handleSelect(course.id)}
                    class={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${
                      isSelected
                        ? 'bg-indigo-50'
                        : noPrices
                          ? 'hover:bg-gray-50 opacity-60'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span
                          class={`text-sm font-medium truncate ${isSelected ? 'text-indigo-700' : 'text-gray-900'}`}
                        >
                          {course.title}
                        </span>
                        {course.isBundle && <BundleBadge />}
                      </div>
                      <div class="flex items-center gap-3 mt-0.5">
                        {course.guideName && (
                          <span class="text-xs text-gray-500 font-medium">
                            by {course.guideName}
                          </span>
                        )}
                        <span class="text-xs text-gray-400">
                          {course.studentCount} {course.studentCount === 1 ? 'student' : 'students'}
                        </span>
                        <span class="text-xs text-gray-400">
                          {course.priceCount} {course.priceCount === 1 ? 'price' : 'prices'}
                        </span>
                        <span class="text-xs text-gray-400">{formatDate(course.createdAt)}</span>
                      </div>
                    </div>
                    {isSelected && (
                      <span class="text-indigo-600 text-sm font-bold flex-shrink-0 mt-0.5">✓</span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {value && (
            <div class="border-t border-gray-100 p-2">
              <button
                type="button"
                onClick={() => handleSelect('')}
                class="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
